import React from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Image,
  Megaphone,
  MessageSquare,
  AtSign,
  BarChart3,
  FileBarChart2,
  BookOpen,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  Users,
  Share2,
  Mic,
  UserCircle,
  Bot,
  PenSquare,
  CheckSquare,
  Radio,
  TrendingUp,
  Shield,
  Bell,
  ShieldCheck,
  GitBranch,
  CreditCard,
  Webhook,
  Target,
  Palette,
  UsersRound,
  Eye,
} from 'lucide-react';
import { cn, Badge } from '@mellify/ui';
import { useSidebarStore } from '../../stores/sidebarStore';
import { SidebarNavItem } from './SidebarNavItem';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { SidebarChannelList } from '../../pages/dashboard/channels/components/SidebarChannelList';
import { useWorkspaceRole } from '../../hooks/useWorkspaceRole';
import { MellifyLogo } from '../public/MellifyLogo';
import { ROUTES } from '../../routes/routeConfig';

export interface SidebarProps {
  /** Currently active route path for highlighting */
  activePath?: string;
  /** Additional className */
  className?: string;
}

interface NavSection {
  label?: string;
  items: {
    label: string;
    icon: React.ReactNode;
    path: string;
    badge?: React.ReactNode;
    /** If specified, only show this item to users with one of these roles */
    roles?: string[];
  }[];
}

const navigationSections: NavSection[] = [
  {
    items: [
      {
        label: 'Dashboard',
        icon: <LayoutDashboard className="w-5 h-5" />,
        path: '/dashboard',
      },
    ],
  },
  {
    label: 'Content',
    items: [
      {
        label: 'Content',
        icon: <PenSquare className="w-5 h-5" />,
        path: '/dashboard/content',
      },
      {
        label: 'Approval Queue',
        icon: <CheckSquare className="w-5 h-5" />,
        path: '/dashboard/content/approval-queue',
      },
      {
        label: 'Posts',
        icon: <FileText className="w-5 h-5" />,
        path: '/dashboard/posts',
      },
      {
        label: 'Calendar',
        icon: <Calendar className="w-5 h-5" />,
        path: '/dashboard/calendar',
      },
      {
        label: 'Publishing',
        icon: <Radio className="w-5 h-5" />,
        path: '/dashboard/publishing',
      },
      {
        label: 'Media Library',
        icon: <Image className="w-5 h-5" />,
        path: '/dashboard/media',
      },
    ],
  },
  {
    label: 'Channels',
    items: [
      {
        label: 'Channels',
        icon: <Share2 className="w-5 h-5" />,
        path: '/dashboard/channels',
      },
    ],
  },
  {
    label: 'Marketing',
    items: [
      {
        label: 'Campaigns',
        icon: <Megaphone className="w-5 h-5" />,
        path: '/dashboard/campaigns',
      },
    ],
  },
  {
    label: 'Ad Manager',
    items: [
      {
        label: 'Ad Manager',
        icon: <Target className="w-5 h-5" />,
        path: '/dashboard/ad-manager',
        roles: ['owner', 'admin', 'manager'],
      },
      {
        label: 'Ad Campaigns',
        icon: <Megaphone className="w-5 h-5" />,
        path: '/dashboard/ad-manager/campaigns',
        roles: ['owner', 'admin', 'manager'],
      },
      {
        label: 'Creatives',
        icon: <Palette className="w-5 h-5" />,
        path: '/dashboard/ad-manager/creatives',
        roles: ['owner', 'admin', 'manager'],
      },
      {
        label: 'Audiences',
        icon: <UsersRound className="w-5 h-5" />,
        path: '/dashboard/ad-manager/audiences',
        roles: ['owner', 'admin', 'manager'],
      },
      {
        label: 'Ad Insights',
        icon: <Eye className="w-5 h-5" />,
        path: '/dashboard/ad-manager/insights',
        roles: ['owner', 'admin', 'manager'],
      },
    ],
  },
  {
    label: 'Engagement',
    items: [
      {
        label: 'Inbox',
        icon: <MessageSquare className="w-5 h-5" />,
        path: '/dashboard/inbox',
        badge: (
          <Badge variant="danger" size="sm">
            3
          </Badge>
        ),
      },
      {
        label: 'Notifications',
        icon: <Bell className="w-5 h-5" />,
        path: '/dashboard/notifications',
      },
      {
        label: 'Trends & Alerts',
        icon: <TrendingUp className="w-5 h-5" />,
        path: '/dashboard/trends',
      },
      {
        label: 'Crisis Playbooks',
        icon: <Shield className="w-5 h-5" />,
        path: '/dashboard/trends/playbooks',
      },
      {
        label: 'Mentions',
        icon: <AtSign className="w-5 h-5" />,
        path: '/dashboard/mentions',
      },
    ],
  },
  {
    label: 'Insights',
    items: [
      {
        label: 'Analytics',
        icon: <BarChart3 className="w-5 h-5" />,
        path: '/dashboard/analytics',
      },
      {
        label: 'Reports',
        icon: <FileBarChart2 className="w-5 h-5" />,
        path: '/dashboard/reports',
      },
    ],
  },
  {
    label: 'Knowledge',
    items: [
      {
        label: 'Knowledge Base',
        icon: <BookOpen className="w-5 h-5" />,
        path: '/dashboard/knowledge',
      },
      {
        label: 'Brand Voice',
        icon: <Mic className="w-5 h-5" />,
        path: '/dashboard/brand-voice',
      },
      {
        label: 'Personas',
        icon: <UserCircle className="w-5 h-5" />,
        path: '/dashboard/personas',
      },
    ],
  },
  {
    label: 'AI',
    items: [
      {
        label: 'AI Agents',
        icon: <Bot className="w-5 h-5" />,
        path: '/dashboard/ai-agents',
      },
    ],
  },
  {
    label: 'Management',
    items: [
      {
        label: 'Administration',
        icon: <ShieldCheck className="w-5 h-5" />,
        path: '/dashboard/admin',
      },
      {
        label: 'Approval Workflows',
        icon: <GitBranch className="w-5 h-5" />,
        path: '/dashboard/admin/approval-workflows',
        roles: ['owner', 'admin'],
      },
      {
        label: 'Webhooks',
        icon: <Webhook className="w-5 h-5" />,
        path: '/dashboard/admin/webhooks',
        roles: ['owner', 'admin'],
      },
      {
        label: 'Billing',
        icon: <CreditCard className="w-5 h-5" />,
        path: '/dashboard/settings/billing',
        roles: ['owner', 'admin'],
      },
      {
        label: 'Team',
        icon: <Users className="w-5 h-5" />,
        path: '/dashboard/settings/team',
      },
    ],
  },
];

const bottomNavItems = [
  {
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    path: '/dashboard/settings',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ activePath = '/dashboard', className }) => {
  const { isCollapsed, toggle } = useSidebarStore();
  const { role } = useWorkspaceRole();

  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-white dark:bg-dark-900 border-r border-dark-200 dark:border-dark-700 transition-all duration-300 ease-smooth overflow-hidden',
        isCollapsed ? 'w-[68px]' : 'w-64',
        className
      )}
    >
      {/* Logo & Workspace Switcher */}
      <div className={cn('shrink-0 p-3', isCollapsed ? 'px-2' : 'px-3')}>
        {/* Logo */}
        <div
          className={cn(
            'flex items-center gap-2 mb-4',
            isCollapsed ? 'justify-center' : 'px-1'
          )}
        >
          <Link to={ROUTES.DASHBOARD} className="flex items-center no-underline">
            <MellifyLogo size="sm" showText={!isCollapsed} />
          </Link>
        </div>

        {/* Workspace Switcher */}
        <WorkspaceSwitcher isCollapsed={isCollapsed} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin px-2 py-2">
        <div className="flex flex-col gap-6">
          {navigationSections.map((section, sectionIndex) => (
            <div key={section.label || `section-${sectionIndex}`}>
              {section.label && !isCollapsed && (
                <p className="px-3 mb-1 text-xs font-semibold text-dark-400 dark:text-dark-500 uppercase tracking-wider">
                  {section.label}
                </p>
              )}
              {section.label && isCollapsed && (
                <div className="mx-auto mb-1 w-6 h-px bg-dark-200 dark:bg-dark-700" />
              )}
              <div className="flex flex-col gap-0.5">
                {section.items
                  .filter((item) => {
                    // If item has role restrictions, check if user has the required role
                    if (item.roles && item.roles.length > 0) {
                      return role ? item.roles.includes(role) : false;
                    }
                    return true;
                  })
                  .map((item) => (
                    <SidebarNavItem
                      key={item.path}
                      label={item.label}
                      icon={item.icon}
                      isActive={activePath === item.path}
                      isCollapsed={isCollapsed}
                      badge={item.badge}
                      href={item.path}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* Connected Channels List */}
        <SidebarChannelList collapsed={isCollapsed} />
      </nav>

      {/* Bottom section */}
      <div className="shrink-0 border-t border-dark-200 dark:border-dark-700 px-2 py-2">
        <div className="flex flex-col gap-0.5">
          {bottomNavItems.map((item) => (
            <SidebarNavItem
              key={item.path}
              label={item.label}
              icon={item.icon}
              isActive={activePath === item.path}
              isCollapsed={isCollapsed}
              href={item.path}
            />
          ))}
        </div>

        {/* Collapse Toggle */}
        <button
          type="button"
          onClick={toggle}
          className={cn(
            'flex items-center gap-3 w-full rounded-lg px-3 py-2 mt-1 text-dark-400 dark:text-dark-500 hover:text-dark-700 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700/50 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isCollapsed && 'justify-center px-2'
          )}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronsRight className="w-5 h-5 shrink-0" />
          ) : (
            <>
              <ChevronsLeft className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};

Sidebar.displayName = 'Sidebar';